import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

export default function initHeader() {
    const header = document.querySelector('.header');
    if (!header) return;

    let lastScrollY = window.scrollY;
    let isHidden = false;
    let navOpen = false;

    // Initial state check
    if (window.scrollY > 50) {
        header.classList.add('header--scrolled');
    }

    window.addEventListener('scroll', () => {
        if (navOpen) return;

        const currentScrollY = window.scrollY;
        const scrollDelta = currentScrollY - lastScrollY;

        // On scroll down
        if (scrollDelta > 5 && currentScrollY > 20 && !isHidden) {
            gsap.to(header, {
                yPercent: -100,
                duration: 0.4,
                ease: 'power2.inOut',
                onComplete: () => {
                    isHidden = true;
                }
            });
        }

        // On scroll up
        else if (scrollDelta < -5 && isHidden) {
            header.classList.add('header--scrolled');
            gsap.to(header, {
                yPercent: 0,
                duration: 0.4,
                ease: 'power2.out',
                onComplete: () => {
                    isHidden = false;
                }
            });
        }

        // Back to top
        if (currentScrollY <= 50) {
            header.classList.remove('header--scrolled');
            if (isHidden) {
                gsap.to(header, {
                    yPercent: 0,
                    duration: 0.4,
                    ease: 'power2.out',
                    onComplete: () => {
                        isHidden = false;
                    }
                });
            }
        }

        lastScrollY = currentScrollY;
    }, { passive: true });

    // Submenu accessibility & Mobile Accordion
    const menuItems = header.querySelectorAll('.header__nav__menu__item');
    const burger = header.querySelector('.header__burger');
    const nav = header.querySelector('.header__nav');

    const isMobile = () => window.innerWidth <= 1024;

    if (burger && nav) {
        let lockedScrollY = 0;

        const lockScroll = () => {
            lockedScrollY = window.scrollY;
            document.body.style.position = 'fixed';
            document.body.style.top = `-${lockedScrollY}px`;
            document.body.style.left = '0';
            document.body.style.right = '0';
            document.body.style.width = '100%';
        };

        const unlockScroll = () => {
            document.body.style.position = '';
            document.body.style.top = '';
            document.body.style.left = '';
            document.body.style.right = '';
            document.body.style.width = '';
            window.scrollTo(0, lockedScrollY);
            // Restore the baseline so the scroll event fired by scrollTo() reads a
            // zero delta and doesn't trigger a spurious hide/show animation.
            lastScrollY = lockedScrollY;
            // Lenis isn't stopped (so the open menu scrolls natively), but the
            // background didn't move while locked — resync it to avoid a jump.
            if (window.lenis) window.lenis.scrollTo(lockedScrollY, { immediate: true });
        };

        const openNav = () => {
            burger.setAttribute('aria-expanded', 'true');
            nav.classList.add('is-open');
            navOpen = true;
            gsap.killTweensOf(header);
            gsap.set(header, { clearProps: 'transform' });
            isHidden = false;
            // Tell Lenis to ignore scroll inside the overlay so it scrolls
            // natively (otherwise Lenis hijacks the wheel/touch and it can't).
            nav.setAttribute('data-lenis-prevent', '');
            lockScroll();
        };

        const closeNav = () => {
            burger.setAttribute('aria-expanded', 'false');
            nav.classList.remove('is-open');
            nav.removeAttribute('data-lenis-prevent');
            navOpen = false;
            unlockScroll();
            burger.focus();
        };

        burger.addEventListener('click', () => {
            const isOpen = burger.getAttribute('aria-expanded') === 'true';
            if (isOpen) {
                closeNav();
            } else {
                openNav();
            }
        });

        // Focus trap & ESC to close
        document.addEventListener('keydown', (e) => {
            if (!nav.classList.contains('is-open')) return;

            if (e.key === 'Escape') {
                closeNav();
                return;
            }

            if (e.key === 'Tab') {
                const focusable = nav.querySelectorAll('a[href], button, input, select, textarea, [tabindex]:not([tabindex="-1"])');
                const elements = Array.from(focusable).filter(el => {
                    return el.offsetParent !== null && !el.disabled;
                });
                if (elements.length === 0) return;

                const first = elements[0];
                const last = elements[elements.length - 1];

                if (e.shiftKey && document.activeElement === first) {
                    e.preventDefault();
                    last.focus();
                } else if (!e.shiftKey && document.activeElement === last) {
                    e.preventDefault();
                    first.focus();
                }
            }
        });
    }

    menuItems.forEach(item => {
        const toggle = item.querySelector('.header__nav__menu__item__toggle');
        const submenu = item.querySelector('.submenu');

        if (toggle && submenu) {
            const setExpanded = (expanded) => {
                toggle.setAttribute('aria-expanded', expanded);
            };

            // Desktop: open submenu on hover, close others
            item.addEventListener('mouseenter', () => {
                if (isMobile()) return;
                menuItems.forEach(otherItem => {
                    if (otherItem !== item) {
                        const otherToggle = otherItem.querySelector('.header__nav__menu__item__toggle');
                        if (otherToggle && otherToggle.getAttribute('aria-expanded') === 'true') {
                            otherToggle.setAttribute('aria-expanded', 'false');
                        }
                    }
                });
                setExpanded('true');
            });

            // Desktop: close submenu when mouse leaves the item
            item.addEventListener('mouseleave', () => {
                if (isMobile()) return;
                setExpanded('false');
            });

            // Desktop: close submenu when focus leaves the menu item entirely
            item.addEventListener('focusout', (e) => {
                if (!isMobile() && !item.contains(e.relatedTarget)) {
                    setExpanded('false');
                }
            });

            // Desktop: close submenu on Escape, return focus to toggle
            item.addEventListener('keydown', (e) => {
                if (!isMobile() && e.key === 'Escape' && toggle.getAttribute('aria-expanded') === 'true') {
                    setExpanded('false');
                    toggle.focus();
                }
            });

            // Toggle button opens/closes the submenu (link navigates normally)
            toggle.addEventListener('click', (e) => {
                e.preventDefault();
                const isOpen = toggle.getAttribute('aria-expanded') === 'true';

                if (isMobile()) {
                    // Close other open submenus
                    menuItems.forEach(otherItem => {
                        if (otherItem !== item) {
                            const otherToggle = otherItem.querySelector('.header__nav__menu__item__toggle');
                            const otherSubmenu = otherItem.querySelector('.submenu');
                            if (otherToggle && otherSubmenu && otherToggle.getAttribute('aria-expanded') === 'true') {
                                otherToggle.setAttribute('aria-expanded', 'false');
                                gsap.killTweensOf(otherSubmenu);
                                gsap.to(otherSubmenu, {
                                    height: 0,
                                    duration: 0.3,
                                    ease: 'power2.inOut',
                                    onComplete: () => {
                                        otherSubmenu.style.display = 'none';
                                    }
                                });
                            }
                        }
                    });

                    setExpanded(String(!isOpen));
                    gsap.killTweensOf(submenu);
                    if (!isOpen) {
                        submenu.style.display = 'block';
                        gsap.fromTo(submenu,
                            { height: 0, opacity: 0 },
                            {
                                height: 'auto',
                                opacity: 1,
                                duration: 0.5,
                                ease: 'expo.out'
                            }
                        );
                    } else {
                        gsap.to(submenu, {
                            height: 0,
                            opacity: 0,
                            duration: 0.4,
                            ease: 'expo.inOut',
                            onComplete: () => {
                                submenu.style.display = 'none';
                            }
                        });
                    }
                } else {
                    // Desktop: close other open submenus, then toggle this one
                    menuItems.forEach(otherItem => {
                        if (otherItem !== item) {
                            const otherToggle = otherItem.querySelector('.header__nav__menu__item__toggle');
                            if (otherToggle && otherToggle.getAttribute('aria-expanded') === 'true') {
                                otherToggle.setAttribute('aria-expanded', 'false');
                            }
                        }
                    });

                    setExpanded(isOpen ? 'false' : 'true');
                }
            });
        }
    });

    // Close desktop submenus when clicking outside the nav
    document.addEventListener('click', (e) => {
        if (isMobile()) return;
        if (!header.contains(e.target)) {
            menuItems.forEach(item => {
                const toggle = item.querySelector('.header__nav__menu__item__toggle');
                if (toggle && toggle.getAttribute('aria-expanded') === 'true') {
                    toggle.setAttribute('aria-expanded', 'false');
                }
            });
        }
    });
}
